import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import { Icon } from '@ohif/ui';

import LoginViewport from '../../components/LoginViewport';
import SvgLoading from '../../components/SvgLoading';
import HttpClient from '../../utils/HttpClient';
import { validatePassword } from '../../utils/validation';


export default function FirstAccess({ challenge, setForm }) {
  const cookies = new Cookies(null, { path: '/' });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isFormValid = useMemo(() => {
    return Boolean(
      formData.password &&
      formData.confirmPassword
    );
  }, [formData.password, formData.confirmPassword]);

  const passwordsMatch = useMemo(() => {
    if (!formData.password || !formData.confirmPassword) return true;
    return formData.password === formData.confirmPassword;
  }, [formData.password, formData.confirmPassword]);

  const handleInputChange = useCallback((field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!isFormValid) return;

    if (!validatePassword(formData.password, formData.confirmPassword)) {
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...challenge,
        password: formData.password,
      };

      const response = await HttpClient.post(
        '/login/?first=true',
        payload,
        cookies.get('token')
      );

      if (!response) {
        toast.error(t('Common:Server Error'));
        return;
      }

      if (response.data) {
        toast.success(t('Login:Password set successfully'), {
          duration: 3000,
        });
        setForm('SignIn');
        navigate('/', { replace: true });
      } else {
        toast.error(response.message || t('Login:Failed to set password'), {
          duration: 3000,
        });
        setForm('SignIn');
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('First access error:', error);
      toast.error(error.response?.data?.message || t('Common:Server Error'));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    isFormValid,
    challenge,
    cookies,
    navigate,
    t,
    setForm
  ]);

  const PasswordInput = useCallback(({
    id,
    label,
    value,
    onChange,
    error = false
  }) => (
    <div className="mt-5">
      <label htmlFor={id} className={`block text-sm font-medium leading-6 mb-2 ${error ? 'text-red-400' : 'text-white'}`}>
        {label}
      </label>

      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          name={id}
          id={id}
          value={value}
          className={`block w-full rounded-xl border-0 py-3.5 px-4 bg-white/5 text-white shadow-sm ring-1 ring-inset ${
            error ? 'ring-red-500 focus:ring-red-500' : 'ring-white/10 focus:ring-blue-500'
          } placeholder:text-slate-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 transition-all duration-200`}
          onChange={onChange}
          disabled={isSubmitting}
          autoComplete="new-password"
          required
        />
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-2"
          onClick={togglePasswordVisibility}
          disabled={isSubmitting}
          aria-label={showPassword ? t('Login:Hide password') : t('Login:Show password')}
        >
          <Icon
            className="h-5 w-5"
            name={showPassword ? 'eye-hidden' : 'eye-visible'}
          />
        </button>
      </div>
    </div>
  ), [showPassword, isSubmitting, togglePasswordVisibility, t]);

  if (!challenge) {
    navigate('/login', { replace: true });
    return null;
  }

  return (
    <LoginViewport>
      <div className="w-full">
        <div className="lg:hidden flex justify-center mb-10">
          <img
            className="h-12 w-auto"
            src="/public/assets/logo-harpiAI.svg"
            alt="HarpiAI"
          />
        </div>

        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            {challenge.username ? (
              <>
                {t('Login:Welcome')}, <i>{challenge.username}</i>
              </>
            ) : t('Login:Sign in')}
          </h2>

          <p className="mt-2 text-md text-white">
            {t('Login:This is your first login')}
          </p>
        </div>

        <div className="mt-8">
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-6">
              <PasswordInput
                id="password"
                label={t('Login:Password')}
                value={formData.password}
                onChange={handleInputChange('password')}
              />

              <PasswordInput
                id="confirmPassword"
                label={t('Login:Confirm Password')}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                error={!passwordsMatch}
              />
            </div>

            {!passwordsMatch && formData.confirmPassword && (
              <p className="text-red-400 text-sm mt-2">
                {t('Login:Must match')}
              </p>
            )}

            <div className="mt-6 p-4 bg-black rounded-xl border border-white/10 text-xs text-white font-semibold">
              <p className="font-medium mb-2">{t('Login:Password requirements')}:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>{t('Login:Must contain at least 8 characters')}</li>
                <li>{t('Login:Must contain at least one lowercase letter')}</li>
                <li>{t('Login:Must contain at least one uppercase letter')}</li>
                <li>{t('Login:Must contain at least one number')}</li>
                <li>{t('Login:Must contain at least one special character')}</li>
              </ul>
            </div>

            <div className="mt-8">
              <button
                disabled={isSubmitting || !isFormValid || !passwordsMatch}
                type="submit"
                className="flex w-full justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3.5 text-sm font-semibold text-white shadow-md hover:from-blue-500 hover:to-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform active:scale-[0.98]"
              >
                {isSubmitting ? <SvgLoading /> : t('Login:Sign in')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </LoginViewport>
  );
};
